import type { EventBus } from "../core/EventBus.ts";
import type { DraftSystem } from "./DraftSystem.ts";
import type { EconomyTuning } from "../entities/types.ts";

/** Single source of truth for sell refund ratio. */
export const SELL_MULTIPLIER = 0.7; // CR-15 (C4): Raised from 0.5

/**
 * S141 / BL-124 / D-236 — Lever 3: the surplus cap (the anti-runaway).
 * Softens the per-wave BONUS (never kill rewards) by how much gold is banked,
 * so a player who SITS on a war chest sees the passive bonus dwindle, while a
 * player who keeps reinvesting (gold below threshold) never feels the dip.
 * Owner Gateway-A steer: floor 0.30 ("overshoot-then-soften").
 *   ≤ 1500 gold → ×1.00 (full bonus; the spending player)
 *   ≥ 3500 gold → ×0.30 (floored; the banking player)
 * Linear between. Kill rewards are untouched — you can always EARN your way to
 * a buy; only the passive bonus floors. (Diagnosis §4.1 / bible §3.6.)
 */
export const SURPLUS_THRESHOLD = 1500;
export const SURPLUS_RANGE = 2000;
export const SURPLUS_FLOOR = 0.3;
/** Act II onward — Act I income (and the W1-5 / W1-3 golden masters) is sacred. */
export const ECONOMY_CAP_START_WAVE = 13;

export function surplusMultiplier(gold: number): number {
  if (gold <= SURPLUS_THRESHOLD) return 1;
  const t = Math.min(1, (gold - SURPLUS_THRESHOLD) / SURPLUS_RANGE);
  return 1 - (1 - SURPLUS_FLOOR) * t;
}

/**
 * S156 / PT-41 / D-253 — the resolved (all-fields-present) economy tuning the
 * EconomySystem runs on. The MODULARITY lift: the surplus cap + the new
 * kill-gold ramp are now a single central DATA block (`waves.json` `economy`),
 * re-tunable in one line with no rebuild. `EconomyTuning` (the optional data
 * shape, `entities/types.ts`) → resolved here against the code default.
 */
export interface KillGoldRamp {
  fullUntilWave: number;
  floor: number;
  floorWave: number;
}
export interface EconomyConfig {
  surplusThreshold: number;
  surplusRange: number;
  surplusFloor: number;
  capStartWave: number;
  killGoldRamp: KillGoldRamp;
  /** Spec B S-4 — the wave-clear bonus LATE SLOPE, data-driven
   *  (`waves.json` economy.waveBonus). The checkpoint measured pressure
   *  outrunning the wallet ~11× across W14-35 (spawned÷earned 0.27 → 3.13 on
   *  the zero-kill floor) because the base ladder `30 + 5w` is flat against
   *  ~×23 volume growth. From `lateStartWave`, each wave adds
   *  `(w - lateStartWave + 1) * latePerWave` on top of the base — BEFORE the
   *  surplus softener, which still binds banked boards exactly as D-236
   *  intends. The identity default (latePerWave 0) is byte-identical to the
   *  pre-S-4 formula. `base`/`perWave` are the flat ladder itself (D-322 /
   *  AC-5 (Task-007) data-lift — the pre-existing `30 + 5w` literal). */
  waveBonus: {
    base: number;
    perWave: number;
    lateStartWave: number;
    latePerWave: number;
  };
  /** D-322 / AC-5 (Task-007) — the sell refund ratio, data-driven
   *  (`waves.json` economy.sellMultiplier). Byte-safe default = SELL_MULTIPLIER. */
  sellMultiplier: number;
}

/**
 * The code default — byte-identical to the pre-S156 economy. The surplus block
 * mirrors the module constants; the `killGoldRamp` is the IDENTITY (×1.0 every
 * wave: floor 1), so an absent `waves.json` `economy` block leaves kill-gold
 * exactly as it was (the byte-safe fallback). Production `waves.json` ships the
 * real decaying ramp.
 */
export const DEFAULT_ECONOMY_CONFIG: EconomyConfig = {
  surplusThreshold: SURPLUS_THRESHOLD,
  surplusRange: SURPLUS_RANGE,
  surplusFloor: SURPLUS_FLOOR,
  capStartWave: ECONOMY_CAP_START_WAVE,
  killGoldRamp: {
    fullUntilWave: Number.POSITIVE_INFINITY,
    floor: 1,
    floorWave: Number.POSITIVE_INFINITY,
  },
  // S-4 identity: no late slope — bonus stays exactly Math.round(30 + 5w).
  waveBonus: {
    base: 30,
    perWave: 5,
    lateStartWave: Number.POSITIVE_INFINITY,
    latePerWave: 0,
  },
  sellMultiplier: SELL_MULTIPLIER,
};

/** Merge an optional (data-driven) economy override onto the code default.
 *  Absent/empty → the default unchanged (byte-identical fallback). */
export function resolveEconomyConfig(
  override?: EconomyTuning | null,
): EconomyConfig {
  if (!override) return DEFAULT_ECONOMY_CONFIG;
  const d = DEFAULT_ECONOMY_CONFIG;
  const r = override.killGoldRamp;
  return {
    surplusThreshold: override.surplusThreshold ?? d.surplusThreshold,
    surplusRange: override.surplusRange ?? d.surplusRange,
    surplusFloor: override.surplusFloor ?? d.surplusFloor,
    capStartWave: override.capStartWave ?? d.capStartWave,
    killGoldRamp: r
      ? {
          fullUntilWave: r.fullUntilWave ?? d.killGoldRamp.fullUntilWave,
          floor: r.floor ?? d.killGoldRamp.floor,
          floorWave: r.floorWave ?? d.killGoldRamp.floorWave,
        }
      : d.killGoldRamp,
    waveBonus: override.waveBonus
      ? {
          base: override.waveBonus.base ?? d.waveBonus.base,
          perWave: override.waveBonus.perWave ?? d.waveBonus.perWave,
          lateStartWave: override.waveBonus.lateStartWave ?? d.waveBonus.lateStartWave,
          latePerWave: override.waveBonus.latePerWave ?? d.waveBonus.latePerWave,
        }
      : d.waveBonus,
    sellMultiplier: override.sellMultiplier ?? d.sellMultiplier,
  };
}

/**
 * The "Bloons" per-wave KILL-GOLD ramp (owner's headline economy idea): full
 * early, DECAYS late, so the player can't out-FARM the threat — throttled at
 * the source (every kill), stronger than only softening the wave bonus. Linear
 * between `fullUntilWave` (×1.0) and `floorWave` (×`floor`), clamped past it.
 */
export function killGoldMultiplier(wave: number, ramp: KillGoldRamp): number {
  if (wave <= ramp.fullUntilWave) return 1;
  if (wave >= ramp.floorWave) return ramp.floor;
  const t = (wave - ramp.fullUntilWave) / (ramp.floorWave - ramp.fullUntilWave);
  return 1 - (1 - ramp.floor) * t;
}

export class EconomySystem {
  private gold: number;
  private bus: EventBus;
  private draftSystem: DraftSystem | null = null;
  private sellMult = SELL_MULTIPLIER;
  private freeFirstTower_ = false;
  private firstTowerPlaced_ = false;
  private unsubscribers: (() => void)[] = [];
  /** S156 / PT-41 — the resolved economy tuning (surplus cap + kill-gold ramp),
   *  data-driven via `waves.json` `economy`. Default = byte-identical pre-S156. */
  private readonly economyConfig: EconomyConfig;
  /** S156 — the in-progress wave, tracked from `wave.started`, so the per-wave
   *  kill-gold ramp can scale each kill reward at its source. */
  private currentWave_ = 0;

  constructor(
    startGold: number,
    bus: EventBus,
    economyConfig: EconomyConfig = DEFAULT_ECONOMY_CONFIG,
  ) {
    this.gold = startGold;
    this.bus = bus;
    this.economyConfig = economyConfig;
    this.sellMult = economyConfig.sellMultiplier;

    this.unsubscribers.push(
      this.bus.on("wave.started", (data: unknown) => {
        this.currentWave_ = (data as { wave: number }).wave;
      }),
    );

    this.unsubscribers.push(
      this.bus.on("enemy.killed", (data: unknown) => {
        const d = data as { enemy: { reward: number } };
        let reward = d.enemy.reward;
        if (this.draftSystem) {
          reward =
            reward *
            this.draftSystem.getKillGoldMultiplier() *
            this.draftSystem.getGreedGoldMultiplier() * // GREED_PROTOCOL: +gold
            this.draftSystem.getScorchedGoldPenalty(); // SCORCHED_EARTH: -gold
        }
        // S156 / PT-41 — the "Bloons" kill-gold ramp: full early (×1.0 through
        // `fullUntilWave`, so W1-5 stays FUN + byte-identical), decaying late so
        // the player must build STINGILY. The DEFAULT ramp is identity (×1.0
        // every wave), so a missing `economy` data block is byte-identical. The
        // single round() preserves the prior rounding (×1.0 → unchanged).
        reward *= killGoldMultiplier(
          this.currentWave_,
          this.economyConfig.killGoldRamp,
        );
        this.addGold(Math.round(reward));
      }),
    );

    // BL-155 (S158) — the LEECH siphon: the Act II credit-drainer subtracts a
    // bounded chunk of gold per drain tick (clamped at 0 — it can NEVER push you
    // negative / bankrupt you unfairly; the per-instance `drainCap` bounds the
    // total). Emits `economy.leeched` so the HUD pops a `−N` and the in-lane VFX
    // reads (the owner's #1: the drain must be SEEN). Deterministic sim write.
    this.unsubscribers.push(
      this.bus.on("enemy.leech", (data: unknown) => {
        const d = data as { amount: number; enemy: { x: number; y: number } };
        const taken = Math.min(this.gold, Math.max(0, d.amount));
        if (taken <= 0) return;
        this.gold -= taken;
        this.bus.emit("economy.leeched", {
          amount: taken,
          total: this.gold,
          x: d.enemy.x,
          y: d.enemy.y,
        });
      }),
    );

    this.unsubscribers.push(
      this.bus.on("wave.completed", (data: unknown) => {
        const d = data as { wave: number };
        const w = d.wave;
        // CR-3 (lever B): tighten the per-wave bonus — drop the quadratic term
        // and lower the base so income no longer outruns a maxed board by ~W18
        // (gold piling up idle). A maxed board should now land ~W28-30.
        // S-4 (Spec B): the data-driven late slope on top of the flat base
        // ladder — see EconomyConfig.waveBonus. Identity when absent.
        const wb = this.economyConfig.waveBonus;
        const lateBoost =
          w >= wb.lateStartWave ? (w - wb.lateStartWave + 1) * wb.latePerWave : 0;
        const base = wb.base + w * wb.perWave + lateBoost;
        // S141 / D-236 — Lever 3: soften the bonus by banked surplus so the
        // runaway is bounded (the banking player can't out-bank the threat).
        // Gated to Act II+ (w >= capStartWave): Act I income is sacred (the
        // tutorial arc + the W1-5 / W1-3 golden masters stay byte-identical),
        // and the runaway only matters from ~W12. Below the gate the bonus is
        // exactly `Math.round(30+5w)`. S156: the cap is now data-driven, default
        // byte-identical.
        const bonus =
          w >= this.economyConfig.capStartWave
            ? Math.round(base * this.surplusMult(this.gold))
            : Math.round(base);
        this.addGold(bonus);
      }),
    );
  }

  /** The surplus BONUS softener, using the resolved (data-driven) cap config.
   *  When the config is the default, identical to the exported `surplusMultiplier`. */
  private surplusMult(gold: number): number {
    const { surplusThreshold, surplusRange, surplusFloor } = this.economyConfig;
    if (gold <= surplusThreshold) return 1;
    const t = Math.min(1, (gold - surplusThreshold) / surplusRange);
    return 1 - (1 - surplusFloor) * t;
  }

  destroy(): void {
    for (const unsub of this.unsubscribers) unsub();
    this.unsubscribers.length = 0;
  }

  setDraftSystem(ds: DraftSystem): void {
    this.draftSystem = ds;
  }

  set sellMultiplier(val: number) {
    this.sellMult = val;
  }

  set freeFirstTower(val: boolean) {
    this.freeFirstTower_ = val;
    this.firstTowerPlaced_ = false;
  }

  get freeFirstTower(): boolean {
    return this.freeFirstTower_;
  }

  /**
   * Returns true if the next tower placement should be free.
   * Consumes the free tower (one-time use).
   */
  consumeFreeTower(): boolean {
    if (this.freeFirstTower_ && !this.firstTowerPlaced_) {
      this.firstTowerPlaced_ = true;
      return true;
    }
    return false;
  }

  get currentGold(): number {
    return this.gold;
  }

  /** Grant non-earned run-start credits (module/operator bonuses). Emits
   *  `economy.bonus` (NOT `economy.earned`) so bonuses never inflate
   *  earned-gold stats. The single sanctioned path for raw credit grants. */
  grantStartingCredits(amount: number, reason: string): void {
    this.gold += amount;
    this.bus.emit("economy.bonus", { amount, total: this.gold, reason });
  }

  /** Reset all mutable state for a new run. */
  reset(startGold: number): void {
    this.gold = startGold;
    this.sellMult = this.economyConfig.sellMultiplier;
    this.freeFirstTower_ = false;
    this.firstTowerPlaced_ = false;
    this.currentWave_ = 0;
  }

  canAfford(cost: number): boolean {
    return this.gold >= cost;
  }

  spend(cost: number): boolean {
    if (!this.canAfford(cost)) return false;
    this.gold -= cost;
    this.bus.emit("economy.spent", { amount: cost, remaining: this.gold });
    return true;
  }

  addGold(amount: number): void {
    this.gold += amount;
    this.bus.emit("economy.earned", { amount, total: this.gold });
  }

  /**
   * Refund for selling a tower. `invested` is the tower's `totalInvested` —
   * the HONEST cumulative spend on it. A free-placed tower (the
   * `free_first_tower` module) is recorded with `totalInvested = 0` at
   * placement (see commitTower), and its upgrades still accrue real spend, so
   * this single model correctly gives it a 0 base refund without a special
   * case. (BL-254: a previous `wasFree` parameter was dead — never passed —
   * and implied a guard that wasn't wired; removed to keep the contract honest.)
   */
  getSellPrice(invested: number): number {
    return Math.floor(invested * this.sellMult);
  }
}
